import { Gantt } from "@/shared/components/gantt";
import type { GanttColumn, GanttRow } from "@/shared/components/gantt";
import { RotuloObra } from "@/shared/components/rotulo-obra";
import { TipoObraTags } from "@/shared/components/tipo-obra-tags";

import { ESCOPO_VAZIO } from "../../carga-fornecedor-gantt-rows";
import {
  groupByObra,
  lineRowMinHeight,
  marcaDaLinha,
  rowActions,
  summaryItem,
} from "../../gantt-fornecedor.utils";
import type { ObraGroupRows } from "../../gantt-fornecedor.utils";
import { GanttFornecedorBarraObra } from "../gantt-fornecedor-barra-obra";
import { GanttFornecedorConteudo } from "../gantt-fornecedor-conteudo";
import { GanttFornecedorNome } from "../gantt-fornecedor-nome";
import { SIMULACAO_MARCA_CLASSES } from "@/shared/components/simulacao-marca";
import type { GanttFornecedorObrasViewModel } from "./types-gantt-fornecedor-obras";
import ganttStyles from "@/shared/components/gantt-carga/gantt.module.scss";
import styles from "../../carga-fornecedor-gantt.module.scss";

interface ObraIdentityProps {
  obra: ObraGroupRows["obra"];
  expandable: boolean;
  isOpen: boolean;
  onToggle: () => void;
}

/** Nome da obra (com a seta, quando expande) e as tags dos tipos abaixo. */
function ObraIdentity({ obra, expandable, isOpen, onToggle }: ObraIdentityProps) {
  return (
    <span className={styles.obraIdentity} title={obra.rotulo.sigla}>
      <span className={styles.obraTitle}>
        {expandable ? (
          <button
            type="button"
            className={styles.toggle}
            onClick={onToggle}
            aria-expanded={isOpen}
            aria-label={`${isOpen ? "Recolher" : "Expandir"} ${obra.rotulo.nome}`}
          >
            <i className={`pi ${isOpen ? "pi-chevron-down" : "pi-chevron-right"}`} aria-hidden />
          </button>
        ) : (
          <span className={styles.toggleSpacer} aria-hidden />
        )}
        <RotuloObra {...obra.rotulo} className={styles.name} />
      </span>
      {obra.tipos.length > 0 ? <TipoObraTags tipos={obra.tipos} /> : null}
    </span>
  );
}

/**
 * Gantt do Fornecedor agrupado por obra: colunas Obra | Escopo Macro | Fornecedor (no mobile,
 * o escopo desce para baixo do fornecedor). Cada obra é uma linha só; com mais de uma
 * alocação ela expande nas linhas dos fornecedores. Usa o mesmo `Gantt.Root`.
 */
export function GanttFornecedorObrasView({
  rows,
  marcas,
  today,
  onConflictHover,
  onConflictLeave,
  onResolverConflito,
  scale,
  isMobile,
  unitMinWidth,
  expandida,
  alternar,
}: GanttFornecedorObrasViewModel) {
  const obraColumn: GanttColumn = {
    key: "obra",
    header: "Obra",
    width: "var(--col-obra)",
    cellClassName: styles.obraCell,
  };
  const fornecedorColumn: GanttColumn = {
    key: "fornecedor",
    header: "Fornecedor",
    width: "var(--col-fornecedor)",
    cellClassName: styles.nameCell,
  };
  const columns: GanttColumn[] = isMobile
    ? [obraColumn, fornecedorColumn]
    : [
        obraColumn,
        {
          key: "escopo",
          header: "Escopo Macro",
          width: "var(--col-escopo)",
          cellClassName: styles.nameCell,
        },
        fornecedorColumn,
      ];

  const tableRows: GanttRow[] = groupByObra(rows).flatMap((group) => {
    const expandable = group.rows.length > 1;
    const isOpen = expandida(group.key);
    const identity = (
      <ObraIdentity
        key="obra"
        obra={group.obra}
        expandable={expandable}
        isOpen={isOpen}
        onToggle={() => alternar(group.key)}
      />
    );

    if (expandable && !isOpen) {
      const fornecedores = new Set(group.rows.map((row) => row.title));
      const fornecedorLabel =
        fornecedores.size === 1
          ? (group.rows[0]?.title ?? "")
          : `${fornecedores.size} Fornecedores`;
      const escopos = new Set(
        group.rows.map((row) => row.escopoMacro).filter((escopo) => escopo !== ESCOPO_VAZIO),
      );
      const escopoLabel =
        escopos.size === 0
          ? ESCOPO_VAZIO
          : `${escopos.size} ${escopos.size === 1 ? "Escopo Macro" : "Escopos Macro"}`;
      const item = summaryItem(group);
      return [
        {
          key: `${group.key}-resumo`,
          testId: "gantt-row-obra-resumo",
          className: group.rows.some((row) => marcaDaLinha(marcas, row))
            ? SIMULACAO_MARCA_CLASSES.linha
            : undefined,
          minHeight: lineRowMinHeight(1),
          actionLabel: `Detalhes de ${group.obra.rotulo.nome}`,
          cells: isMobile
            ? [
                identity,
                <span key="resumo" className={styles.stack}>
                  <span className={styles.name}>{fornecedorLabel}</span>
                  <span className={styles.subtitle}>{escopoLabel}</span>
                </span>,
              ]
            : [
                identity,
                <span key="escopo" className={styles.name}>
                  {escopoLabel}
                </span>,
                <span key="resumo" className={styles.name}>
                  {fornecedorLabel}
                </span>,
              ],
          content: item ? (
            <GanttFornecedorBarraObra
              item={item}
              marca={group.rows.map((row) => marcaDaLinha(marcas, row)).find(Boolean)}
            />
          ) : null,
        },
      ];
    }

    return group.rows.map((row, index): GanttRow => {
      const obra = index === 0 ? identity : null;
      const actions = rowActions(
        row.conflicts > 0,
        onResolverConflito && (() => onResolverConflito(row)),
      );
      return {
        key: row.key,
        testId: "gantt-row-obra",
        className: marcaDaLinha(marcas, row) ? SIMULACAO_MARCA_CLASSES.linha : undefined,
        minHeight: lineRowMinHeight(row.lanes),
        actionLabel: actions ? `Ações de ${row.title}` : `Detalhes de ${row.title}`,
        actions,
        cells: isMobile
          ? [
              obra,
              <span key="fornecedor" className={styles.stack}>
                <GanttFornecedorNome
                  row={row}
                  marca={marcaDaLinha(marcas, row)}
                  onConflictHover={onConflictHover}
                  onConflictLeave={onConflictLeave}
                />
                <span className={styles.subtitle}>{row.escopoMacro}</span>
              </span>,
            ]
          : [
              obra,
              <span key="escopo" className={styles.name} title={row.escopoMacro}>
                {row.escopoMacro}
              </span>,
              <GanttFornecedorNome
                key="fornecedor"
                row={row}
                marca={marcaDaLinha(marcas, row)}
                onConflictHover={onConflictHover}
                onConflictLeave={onConflictLeave}
              />,
            ],
        content: (
          <GanttFornecedorConteudo
            row={row}
            variant="line"
            marcas={marcas}
            onConflictHover={onConflictHover}
            onConflictLeave={onConflictLeave}
          />
        ),
      };
    });
  });

  return (
    <Gantt.Root
      grid="none"
      className={`${ganttStyles.root} ${styles.obraRoot}`}
      corpoRola
      scale={scale}
      today={today}
      todayLabel="Hoje"
      columns={columns}
      rows={tableRows}
      withActions
      unitMinWidth={unitMinWidth}
    />
  );
}
